import {
  createContext, ReactNode, useMemo, useState, Dispatch, SetStateAction, useCallback,
} from 'react';
import { BaseLayerConfig } from 'types/app.types';
import baseLayers from '../../configs/baseLayers';

interface BaseLayerContextValues {
  selectedBaseLayers: BaseLayerConfig[]
  setSelectedBaseLayers: Dispatch<SetStateAction<BaseLayerConfig[]>>,
  addBaseLayer: (layer: BaseLayerConfig) => void,
  removeBaseLayer: (layer: BaseLayerConfig) => void
}

const BaseLayerContext = createContext<BaseLayerContextValues>({
  selectedBaseLayers: [],
  setSelectedBaseLayers: () => {},
  addBaseLayer: () => {},
  removeBaseLayer: () => {},
});

interface MainAppProviderProps {
  children: ReactNode,
}

function BaseLayerProvider({ children } : MainAppProviderProps) {
  const [selectedBaseLayers, setSelectedBaseLayers] = useState<BaseLayerConfig[]>(baseLayers
    .filter(({ defaultSelected }) => defaultSelected));

  const addBaseLayer = (layer: BaseLayerConfig) => {
    setSelectedBaseLayers((prev) => [...prev, layer].sort((a, b) => a.level - b.level));
  };

  const removeBaseLayer = useCallback((layer: BaseLayerConfig) => {
    const index = selectedBaseLayers.findIndex(({ level }) => level === layer.level);
    setSelectedBaseLayers((prev) => {
      const baseLayers = [...prev];
      baseLayers.splice(index, 1);
      return baseLayers;
    });
  }, [selectedBaseLayers]);

  const value = useMemo(() => ({
    selectedBaseLayers,
    setSelectedBaseLayers,
    addBaseLayer,
    removeBaseLayer,
  }), [removeBaseLayer, selectedBaseLayers]);
  return (
    <BaseLayerContext.Provider value={value}>
      {children}
    </BaseLayerContext.Provider>
  );
}

export { BaseLayerProvider };
export default BaseLayerContext;
